import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { EditActionBar } from '../shared/EditActionBar';
import type {
  Driver08Data,
  OpExcellenceFocusRow,
  AreaDirectorScorecardRow,
  NonAwardedWeight,
} from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { formatHighlights } from '../../../utils/Format';

const OUTPUTS = ['Scheda Direttori', 'Punteggio MAP', 'Focus EO', 'Peso filiali non a premio'] as const;

const emptyOpExcellenceFocusRow = (): OpExcellenceFocusRow => ({
  area: '',
  total_branches_in_area: 0,
  op_excellence_branches_below_100: 0,
  pct: 0,
});

const emptyAreaDirectorScorecardRow = (): AreaDirectorScorecardRow => ({
  objectives_section: '',
  objectives: '',
  strategic_drivers: [],
  kpi_with_weights: [],
});

const fmtN     = (n: number) => n.toLocaleString('it-IT');
const fmtPct1  = (n: number) => `${n.toFixed(1)}%`;
const fmtPct2  = (n: number) => `${n.toFixed(2)}%`;
const fmtPctR  = (n: number) => `${Math.round(n)}%`;

const numCls = 'w-16 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-[#1B365D]/50 tabular-nums';
const txtCls = 'w-full min-w-[7rem] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50';
const areaCls = 'w-full min-w-[9rem] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-[11px] text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50';

/* ─── Main ───────────────────────────────────────────────── */
const FocusAreeContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver08Data>(reportId, 8);

  const [data,      setData]      = useState<Driver08Data | null>(null);
  const [savedData, setSavedData] = useState<Driver08Data | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (remoteData) {
      setData(remoteData);
      setSavedData(remoteData);
    }
  }, [remoteData]);

  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  const updateHistogramRow = (i: number, raw: string) => {
    const val = parseInt(raw, 10);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      map_score_histogram: {
        ...prev.map_score_histogram,
        rows: prev.map_score_histogram.rows.map((r, ri) => ri === i ? { ...r, area_directors: val } : r),
      },
    } : prev));
  };
  const updateHistogramBand = (i: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      map_score_histogram: {
        ...prev.map_score_histogram,
        rows: prev.map_score_histogram.rows.map((r, ri) => ri === i ? { ...r, band: value } : r),
      },
    } : prev));
  };

  /* ─── Scheda Direttori d'Area: testo / driver / KPI ─── */
  const updateScorecardText = (i: number, field: 'objectives_section' | 'objectives', value: string) => {
    setData(prev => (prev ? {
      ...prev,
      area_director_scorecard: {
        ...prev.area_director_scorecard,
        rows: prev.area_director_scorecard.rows.map((r, ri) => ri === i ? { ...r, [field]: value } : r),
      },
    } : prev));
  };
  const updateScorecardDriver = (i: number, di: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      area_director_scorecard: {
        ...prev.area_director_scorecard,
        rows: prev.area_director_scorecard.rows.map((r, ri) => ri === i
          ? { ...r, strategic_drivers: r.strategic_drivers.map((d, dj) => dj === di ? value : d) }
          : r),
      },
    } : prev));
  };
  const updateScorecardKpiName = (i: number, ki: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      area_director_scorecard: {
        ...prev.area_director_scorecard,
        rows: prev.area_director_scorecard.rows.map((r, ri) => ri === i
          ? { ...r, kpi_with_weights: r.kpi_with_weights.map((k, kj) => kj === ki ? { ...k, name: value } : k) }
          : r),
      },
    } : prev));
  };
  const updateScorecardKpiWeight = (i: number, ki: number, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      area_director_scorecard: {
        ...prev.area_director_scorecard,
        rows: prev.area_director_scorecard.rows.map((r, ri) => ri === i
          ? { ...r, kpi_with_weights: r.kpi_with_weights.map((k, kj) => kj === ki ? { ...k, weight_pct: val } : k) }
          : r),
      },
    } : prev));
  };

  const addScorecardRow = () => {
    setData(prev => (prev ? {
      ...prev,
      area_director_scorecard: {
        ...prev.area_director_scorecard,
        rows: [...prev.area_director_scorecard.rows, emptyAreaDirectorScorecardRow()],
      },
    } : prev));
  };

  const updateOpFocusRow = (i: number, field: keyof OpExcellenceFocusRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      op_excellence_focus: {
        ...prev.op_excellence_focus,
        rows: prev.op_excellence_focus.rows.map((r, ri) => ri === i ? { ...r, [field]: val } : r),
      },
    } : prev));
  };
  const updateOpFocusRowName = (i: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      op_excellence_focus: {
        ...prev.op_excellence_focus,
        rows: prev.op_excellence_focus.rows.map((r, ri) => ri === i ? { ...r, area: value } : r),
      },
    } : prev));
  };
  const addOpFocusRow = () => {
    setData(prev => (prev ? {
      ...prev,
      op_excellence_focus: {
        ...prev.op_excellence_focus,
        rows: [...prev.op_excellence_focus.rows, emptyOpExcellenceFocusRow()],
      },
    } : prev));
  };
  const updateOpFocusTotals = (field: keyof OpExcellenceFocusRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      op_excellence_focus: { ...prev.op_excellence_focus, totals: { ...prev.op_excellence_focus.totals, [field]: val } },
    } : prev));
  };
  const updateOpFocusTotalsName = (value: string) => {
    setData(prev => (prev ? {
      ...prev,
      op_excellence_focus: { ...prev.op_excellence_focus, totals: { ...prev.op_excellence_focus.totals, area: value } },
    } : prev));
  };

  const updateWeight = (field: keyof NonAwardedWeight, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? { ...prev, non_awarded_weight: { ...prev.non_awarded_weight, [field]: val } } : prev));
  };

  const updateHighlights = (field: 'scorecard_highlights' | 'map_score_highlights' | 'op_excellence_highlights' | 'non_awarded_weight_highlights', value: string) => {
    setData(prev => (prev ? { ...prev, [field]: value } : prev));
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-[#1B365D] rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  const histogram     = data.map_score_histogram;
  const opFocus       = data.op_excellence_focus;
  const weight        = data.non_awarded_weight;
  const totHistogram  = histogram.rows.reduce((s, r) => s + r.area_directors, 0);

  return (
    <div className="p-6 space-y-5">

      {/* KPI strip globale */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{fmtN(data.total_area_directors)}</div>
          <div className="text-[11px] text-gray-400 mt-1">Direttori di Area totali</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-emerald-600">{fmtN(weight.awarded_area_directors)}</div>
          <div className="text-[11px] text-gray-400 mt-1">
            A premio ({fmtPct2(data.award_pct)} dei valutati)
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-amber-600">{fmtPct1(weight.non_awarded_pct)}</div>
          <div className="text-[11px] text-gray-400 mt-1">
            Filiali non a premio nelle aree a premio
          </div>
        </div>
      </div>

      {/* Tab bar + edit bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1.5 p-1 bg-gray-50 rounded-xl border border-gray-100 flex-1 min-w-0">
          {OUTPUTS.map((label, i) => (
            <button
              key={label}
              onClick={() => setActiveTab(i)}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                activeTab === i ? 'bg-[#1B365D] text-white shadow-sm' : 'text-gray-400 hover:text-[#1B365D]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <EditActionBar isEditing={isEditing} onProcedi={() => setIsEditing(true)} onAnnulla={handleAnnulla} onSalva={handleSalva} disabled={isReadOnly} />
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.18 }}>

          {/* ── Output 0: Scheda Direttori (testo introduttivo + tabella obiettivi/KPI/pesi) ── */}
          {activeTab === 0 && (
            <div className="space-y-4">
              <div className="px-4 py-3 rounded-xl bg-[#1B365D]/5 border border-[#1B365D]/10 text-xs text-[#1B365D]/80 leading-relaxed">
                <span className="font-semibold">Highlights: </span>
                {isEditing
                  ? <textarea className="w-full mt-1 p-2 text-xs text-[#1B365D]/80 leading-relaxed border border-[#1B365D]/20 rounded-lg resize-y focus:outline-none focus:border-[#1B365D]/40 bg-white/70 min-h-[160px]" value={data.scorecard_highlights} onChange={e => updateHighlights('scorecard_highlights', e.target.value)} />
                  : <span className="whitespace-pre-wrap">{formatHighlights(data.scorecard_highlights)}</span>}
              </div>

              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-[#1B365D]">Scheda di valutazione Direttori d&apos;Area · {data.period}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                        <th className="text-left px-4 py-2.5 font-semibold w-44">Sezione obiettivi</th>
                        <th className="text-left px-4 py-2.5 font-semibold w-40">Obiettivi</th>
                        <th className="text-left px-4 py-2.5 font-semibold w-56">Driver strategici</th>
                        <th className="text-left px-4 py-2.5 font-semibold">KPI · peso</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.area_director_scorecard.rows.map((row, i) => {
                        const sectionTotal = row.kpi_with_weights.reduce((s, k) => s + k.weight_pct, 0);
                        return (
                          <motion.tr key={i} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }} className="align-top">
                            <td className="px-4 py-3 text-[11px] font-semibold text-[#1B365D] whitespace-pre-wrap">
                              {isEditing
                                ? <textarea className="w-full min-w-[8rem] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-[11px] font-semibold text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50 resize-y min-h-[60px]" value={row.objectives_section} onChange={e => updateScorecardText(i, 'objectives_section', e.target.value)} />
                                : row.objectives_section}
                            </td>
                            <td className="px-4 py-3 text-[11px] text-gray-600 whitespace-pre-wrap">
                              {isEditing
                                ? <textarea className="w-full min-w-[8rem] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-[11px] text-gray-600 focus:outline-none focus:border-[#1B365D]/50 resize-y min-h-[60px]" value={row.objectives} onChange={e => updateScorecardText(i, 'objectives', e.target.value)} />
                                : row.objectives}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {row.strategic_drivers.map((d, di) => (
                                  isEditing
                                    ? <input key={di} type="text" className="w-28 bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-[10px] text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50" value={d} onChange={e => updateScorecardDriver(i, di, e.target.value)} />
                                    : <span key={di} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#1B365D]/5 border border-[#1B365D]/10 text-[#1B365D]/70">
                                        {d}
                                      </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <ul className="space-y-1.5">
                                {row.kpi_with_weights.map((kpi, ki) => (
                                  <li key={ki} className="flex items-start gap-2 text-[11px]">
                                    {isEditing
                                      ? <>
                                          <input type="number" className="w-16 text-right bg-white text-[#1B365D] border border-amber-200 rounded px-1.5 py-0.5 text-[11px] font-bold text-amber-700 focus:outline-none shrink-0 tabular-nums" value={kpi.weight_pct} onChange={e => updateScorecardKpiWeight(i, ki, e.target.value)} />
                                          <input type="text" className="flex-1 min-w-[6rem] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-[11px] text-gray-700 focus:outline-none focus:border-[#1B365D]/50" value={kpi.name} onChange={e => updateScorecardKpiName(i, ki, e.target.value)} />
                                        </>
                                      : <>
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-amber-700 font-bold shrink-0 tabular-nums">{fmtPct1(kpi.weight_pct)}</span>
                                          <span className="text-gray-700 leading-snug">{kpi.name}</span>
                                        </>}
                                  </li>
                                ))}
                              </ul>
                              <div className="mt-2 text-[10px] text-gray-400">
                                Totale sezione: <span className="font-semibold tabular-nums">{fmtPct1(sectionTotal)}</span>
                              </div>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {isEditing && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => addScorecardRow()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Output 1: Punteggio MAP (istogramma per fascia) ── */}
          {activeTab === 1 && (
            <div className="space-y-4">
              <div className="px-4 py-3 rounded-xl bg-[#1B365D]/5 border border-[#1B365D]/10 text-xs text-[#1B365D]/80 leading-relaxed">
                <span className="font-semibold">Highlights: </span>
                {isEditing
                  ? <textarea className="w-full mt-1 p-2 text-xs text-[#1B365D]/80 leading-relaxed border border-[#1B365D]/20 rounded-lg resize-y focus:outline-none focus:border-[#1B365D]/40 bg-white/70 min-h-[100px]" value={data.map_score_highlights} onChange={e => updateHighlights('map_score_highlights', e.target.value)} />
                  : <span className="whitespace-pre-wrap">{formatHighlights(data.map_score_highlights)}</span>}
              </div>

              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-xs font-semibold text-[#1B365D]">Distribuzione Punteggi MAP — Direttori non a premio · {data.period}</p>
                  <span className="text-[11px] text-gray-400">{fmtN(totHistogram)} direttori</span>
                </div>

                <div className="divide-y divide-gray-50">
                  {histogram.rows.map((row, ri) => {
                    const widthPct = totHistogram > 0 ? (row.area_directors / totHistogram) * 100 : 0;
                    return (
                      <div key={ri} className="flex items-center gap-4 px-5 py-3.5">
                        {isEditing
                          ? <input type="text" className="text-sm font-bold w-16 text-[#1B365D] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 focus:outline-none focus:border-[#1B365D]/50" value={row.band} onChange={e => updateHistogramBand(ri, e.target.value)} />
                          : <span className="text-sm font-bold w-16 text-[#1B365D]">{row.band}</span>}
                        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full rounded-full bg-[#1B365D]/40 transition-all" style={{ width: `${widthPct}%` }} />
                        </div>
                        {isEditing
                          ? <input type="number" className={numCls} value={row.area_directors} onChange={e => updateHistogramRow(ri, e.target.value)} />
                          : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full border font-bold text-[11px] bg-amber-50 border-amber-100 text-amber-700">
                              {row.area_directors} {row.area_directors === 1 ? 'direttore' : 'direttori'}
                            </span>
                        }
                      </div>
                    );
                  })}
                </div>

                <div className="bg-[#1B365D] text-white text-[11px] font-semibold px-5 py-3 flex justify-between">
                  <span>Totale non a premio</span>
                  <span>{fmtN(totHistogram)}</span>
                </div>
              </div>

              {/* Sintesi qualification / award */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
                  <div className="text-2xl font-bold text-[#1B365D]">{fmtN(data.qualified_area_directors)} / {fmtN(data.total_area_directors)}</div>
                  <div className="text-[11px] text-gray-400 mt-1">Direttori valutati ({fmtPct2(data.qualification_pct)})</div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-600">{fmtN(weight.awarded_area_directors)} / {fmtN(data.qualified_area_directors)}</div>
                  <div className="text-[11px] text-gray-400 mt-1">Direttori a premio ({fmtPct2(data.award_pct)})</div>
                </div>
              </div>
            </div>
          )}

          {/* ── Output 2: Focus EO ──────────────────────────────── */}
          {activeTab === 2 && (
            <div className="space-y-4">
              <div className="px-4 py-3 rounded-xl bg-[#1B365D]/5 border border-[#1B365D]/10 text-xs text-[#1B365D]/80 leading-relaxed">
                <span className="font-semibold">Highlights: </span>
                {isEditing
                  ? <textarea className="w-full mt-1 p-2 text-xs text-[#1B365D]/80 leading-relaxed border border-[#1B365D]/20 rounded-lg resize-y focus:outline-none focus:border-[#1B365D]/40 bg-white/70 min-h-[100px]" value={data.op_excellence_highlights} onChange={e => updateHighlights('op_excellence_highlights', e.target.value)} />
                  : <span className="whitespace-pre-wrap">{formatHighlights(data.op_excellence_highlights)}</span>}
              </div>

              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-xs font-semibold text-[#1B365D]">Aree Retail a premio con filiali EO &lt; 100 · {data.period}</p>
                  <span className="text-[11px] text-amber-600 font-semibold bg-amber-50 border border-amber-100 px-2.5 py-0.5 rounded-full">
                    {opFocus.rows.length} aree · {fmtN(opFocus.totals.op_excellence_branches_below_100)} filiali EO &lt; 100
                  </span>
                </div>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                        <th className="text-left  px-5 py-2.5 font-semibold">Area</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Tot. filiali dell&apos;area</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-amber-500">Filiali EO &lt; 100</th>
                        <th className="text-right px-4 py-2.5 font-semibold">% su tot. filiali</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {opFocus.rows.map((row, i) => (
                        <motion.tr key={i} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} className="hover:bg-amber-50/20 transition-colors">
                          <td className="px-5 py-2.5 font-medium text-[#1B365D] whitespace-nowrap text-[11px]">
                            {isEditing ? <input type="text" className={txtCls} value={row.area} onChange={e => updateOpFocusRowName(i, e.target.value)} /> : row.area}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-500">
                            {isEditing ? <input type="number" className={numCls} value={row.total_branches_in_area} onChange={e => updateOpFocusRow(i, 'total_branches_in_area', e.target.value)} /> : fmtN(row.total_branches_in_area)}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.op_excellence_branches_below_100} onChange={e => updateOpFocusRow(i, 'op_excellence_branches_below_100', e.target.value)} />
                              : <span className="inline-flex px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100 text-amber-700 font-bold text-[10px]">{row.op_excellence_branches_below_100}</span>}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-500">
                            {isEditing ? <input type="number" className={numCls} value={row.pct} onChange={e => updateOpFocusRow(i, 'pct', e.target.value)} /> : fmtPct1(row.pct)}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#1B365D] text-white text-[11px] font-semibold">
                        <td className="px-5 py-3">
                          {isEditing ? <input type="text" className={areaCls} value={opFocus.totals.area} onChange={e => updateOpFocusTotalsName(e.target.value)} /> : opFocus.totals.area}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isEditing ? <input type="number" className={numCls} value={opFocus.totals.total_branches_in_area} onChange={e => updateOpFocusTotals('total_branches_in_area', e.target.value)} /> : fmtN(opFocus.totals.total_branches_in_area)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isEditing ? <input type="number" className={numCls} value={opFocus.totals.op_excellence_branches_below_100} onChange={e => updateOpFocusTotals('op_excellence_branches_below_100', e.target.value)} /> : fmtN(opFocus.totals.op_excellence_branches_below_100)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isEditing ? <input type="number" className={numCls} value={opFocus.totals.pct} onChange={e => updateOpFocusTotals('pct', e.target.value)} /> : fmtPct1(opFocus.totals.pct)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {isEditing && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => addOpFocusRow()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Output 3: Peso filiali non a premio ─────────────── */}
          {activeTab === 3 && (
            <div className="space-y-4">
              <div className="px-4 py-3 rounded-xl bg-[#1B365D]/5 border border-[#1B365D]/10 text-xs text-[#1B365D]/80 leading-relaxed">
                <span className="font-semibold">Highlights: </span>
                {isEditing
                  ? <textarea className="w-full mt-1 p-2 text-xs text-[#1B365D]/80 leading-relaxed border border-[#1B365D]/20 rounded-lg resize-y focus:outline-none focus:border-[#1B365D]/40 bg-white/70 min-h-[120px]" value={data.non_awarded_weight_highlights} onChange={e => updateHighlights('non_awarded_weight_highlights', e.target.value)} />
                  : <span className="whitespace-pre-wrap">{formatHighlights(data.non_awarded_weight_highlights)}</span>}
              </div>

              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-[#1B365D]">Peso filiali non a premio nelle aree a premio · {data.period}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                        <th className="text-right px-5 py-2.5 font-semibold">Direttori d&apos;Area a premio</th>
                        <th className="text-right px-5 py-2.5 font-semibold">Tot. filiali delle aree a premio</th>
                        <th className="text-right px-5 py-2.5 font-semibold text-amber-500">Filiali non a premio delle aree a premio</th>
                        <th className="text-right px-5 py-2.5 font-semibold text-amber-500">% filiali non a premio</th>
                      </tr>
                    </thead>
                    <tbody>
                      <motion.tr initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} className="bg-white hover:bg-gray-50/60 transition-colors">
                        <td className="px-5 py-4 text-right text-2xl font-bold text-[#1B365D]">
                          {isEditing
                            ? <input type="number" className="w-24 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-2 py-1 text-xl font-bold focus:outline-none" value={weight.awarded_area_directors} onChange={e => updateWeight('awarded_area_directors', e.target.value)} />
                            : fmtN(weight.awarded_area_directors)}
                        </td>
                        <td className="px-5 py-4 text-right text-2xl font-bold text-[#1B365D]">
                          {isEditing
                            ? <input type="number" className="w-24 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-2 py-1 text-xl font-bold focus:outline-none" value={weight.total_branches_in_awarded_areas} onChange={e => updateWeight('total_branches_in_awarded_areas', e.target.value)} />
                            : fmtN(weight.total_branches_in_awarded_areas)}
                        </td>
                        <td className="px-5 py-4 text-right text-2xl font-bold text-amber-600">
                          {isEditing
                            ? <input type="number" className="w-24 text-right bg-white text-[#1B365D] border border-amber-200 rounded px-2 py-1 text-xl font-bold focus:outline-none" value={weight.total_non_awarded_branches} onChange={e => updateWeight('total_non_awarded_branches', e.target.value)} />
                            : fmtN(weight.total_non_awarded_branches)}
                        </td>
                        <td className="px-5 py-4 text-right">
                          {isEditing
                            ? <input type="number" className="w-24 text-right bg-white text-[#1B365D] border border-amber-200 rounded px-2 py-1 text-lg font-bold text-amber-700 focus:outline-none" value={weight.non_awarded_pct} onChange={e => updateWeight('non_awarded_pct', e.target.value)} />
                            : <span className="inline-flex px-3 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-700 font-bold text-lg">{fmtPctR(weight.non_awarded_pct)}</span>}
                        </td>
                      </motion.tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Confronto con periodo precedente, se disponibile */}
              {data.prior_period && (
                <div className="px-4 py-2.5 bg-[#1B365D]/5 border border-[#1B365D]/10 rounded-xl text-[11px] text-[#1B365D]/70">
                  <b>Confronto:</b> il dato {data.period} ({fmtPctR(weight.non_awarded_pct)}) è da confrontare con la rilevazione {data.prior_period} (cfr. testo highlights).
                </div>
              )}
            </div>
          )}

        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default FocusAreeContainer;
